/**
 * The console client of the kernel-rights routes of release-engine (kernel apps, WS-9a,
 * `design-docs/WS-9.md` section 3.1).
 *
 * release-engine (WS-7a and WS-7b) owns seven routes: the kernel-rights page of an app, the request
 * of new rights, the public code-signing key, the site-admin queue, the decision, and the read and
 * the replacement of the release sources. The console calls six of them; the key route returns the
 * same `CodeSigningKeyView` that the page carries already. This module holds the four app routes
 * under `cp:app:read` on one app. `kernelRightsQueue.api.ts` holds the queue and the decision,
 * which `cp:platform:admin` gates.
 *
 * WS-7 keeps its envelopes in its own `kernel-rights.contract.ts` (WS-7 open question 9). So this
 * file copies each name and each field letter for letter, as `appOwners.api.ts` copies `AppOwner`.
 * `kernelRights.wire-parity.test.ts` holds the request bodies equal to `release-engine/openapi.json`.
 *
 * Who calls it: `AppKernelRightsPane`, `KernelRightsRequestForm` and `ReleaseSourcesSection`. Each
 * write goes through `useStepUp()`.
 *
 * Rules that every call keeps:
 *
 * 1. A path id passes `encodeURIComponent`, so an id never splits a path.
 * 2. A write sends `stepUpHeaders(stepUpId)`: no step-up header for `null`, the header for an
 *    approved id, and a throw before any request for a malformed id.
 * 3. A write body holds the WS-7 keys alone, because each WS-7 schema is strict.
 * 4. A non-2xx answer throws `ApiError` with `status` and `body`; a network fault throws the raw
 *    failure. No call retries, caches or stores.
 *
 * The permission constants hide controls and decide nothing. The server decides every write.
 */
import type { CpPermission, KernelRight, ZionJwk } from '@zion/sdk-contracts';
import type { KernelRightRequestStatus } from './kernelRightsQueue.api';
import { stepUpHeaders } from '../platform/auth/step-up';
import { api, apiFetch } from '../platform/http/client';

/** The read check of the app routes and of the manifest entry `app-kernel-rights`. */
export const KERNEL_RIGHTS_READ_PERMISSION = 'cp:app:read' satisfies CpPermission;

/** The check of the request route, on the app (R-011). */
export const KERNEL_RIGHTS_REQUEST_PERMISSION = 'cp:app:kernel-rights:request' satisfies CpPermission;

/** The check of the release-sources PUT route, on the app (WS-7 section 3.7). It equals the request check. */
export const RELEASE_SOURCES_WRITE_PERMISSION = KERNEL_RIGHTS_REQUEST_PERMISSION;

/** The most release sources that one app holds (R-036). */
export const RELEASE_SOURCES_MAX = 5;

/** One kernel-right request as WS-7 shows it. Times are ISO 8601 UTC texts. */
export interface KernelRightRequestView {
  readonly id: string;
  readonly appId: string;
  readonly rights: readonly KernelRight[];
  readonly justification: string;
  readonly status: KernelRightRequestStatus;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly decisionNote: string | null;
  readonly revokedBy: string | null;
  readonly revokedAt: string | null;
  readonly revokeNote: string | null;
  /**
   * The shared bucket location that an approve stored with a request whose rights name
   * `storage.global` (plan 2026-10-13-blob-buckets, R-094 item 3), or `null` for every other
   * request. A revoke keeps it as a record. A release-engine from before stage 5 sends no key, so
   * the console reads an absent key as `null`.
   */
  readonly globalBucketLocation?: string | null;
}

/** The public half of an app's active code-signing key. No field holds private material. */
export interface CodeSigningKeyView {
  readonly kid: string;
  readonly algorithm: 'ES256';
  readonly backend: 'kms' | 'software';
  readonly publicJwk: ZionJwk;
  readonly publicKeyPem: string;
  readonly createdAt: string;
}

/** The kernel-rights page of one app: the granted set, every request newest first, the key or `null`. */
export interface AppKernelRightsView {
  readonly appId: string;
  readonly grantedRights: readonly KernelRight[];
  readonly requests: readonly KernelRightRequestView[];
  readonly codeSigningKey: CodeSigningKeyView | null;
}

/**
 * One live release source of an app: the zion-build runs that may publish a release of the app, and
 * for a kernel app ask for a statement signature (2026-10-15-signed-builds, R-004).
 */
export interface ReleaseSourceView {
  readonly id: string;
  readonly repository: string;
  readonly refPattern: string;
  readonly workflowPath: string;
  readonly createdBy: string;
  readonly createdAt: string;
}

/** One release source as the PUT body carries it: the three keys of WS-7's `releaseSourceSchema`. */
export interface ReleaseSourceInput {
  readonly repository: string;
  readonly refPattern: string;
  readonly workflowPath: string;
}

/** The body of `POST /v1/apps/:appId/kernel-rights/requests`. */
export interface KernelRightRequestBody {
  readonly rights: readonly KernelRight[];
  readonly justification: string;
}

/** The `Content-Type` and step-up headers of one write. */
function writeInit(method: 'POST' | 'PUT', body: unknown, stepUpId: string | null): RequestInit {
  return { method, body: JSON.stringify(body), headers: stepUpHeaders(stepUpId) };
}

/** `/v1/apps/<appId>/kernel-rights`, with the id encoded as one segment. Pure. */
export function appKernelRightsPath(appId: string): string {
  return `/v1/apps/${encodeURIComponent(appId)}/kernel-rights`;
}

/**
 * Read the kernel-rights page of one app: `GET /v1/apps/:appId/kernel-rights`.
 *
 * @param appId the app UUID; WS-7 refuses a slug with 400 (R-100).
 * @throws ApiError on a non-2xx answer (403 with no `cp:app:read` on the app, 404 for an unknown app).
 */
export function getAppKernelRights(appId: string): Promise<AppKernelRightsView> {
  return api.get<AppKernelRightsView>(appKernelRightsPath(appId));
}

/**
 * Ask for kernel rights: `POST /v1/apps/:appId/kernel-rights/requests` with `{ rights, justification }`.
 *
 * @param stepUpId `null` on the first send; the approved step-up request id on the second.
 * @returns the new `pending` request (201).
 * @throws Error('invalid step-up request id') before any request for a malformed id; ApiError on a
 *   non-2xx answer: 400 from the pipe, 403 `step_up_required` on a first send, 403 for no
 *   `cp:app:kernel-rights:request`, 409 for a refused state, 503 when cp-auth-api does not answer.
 */
export async function requestKernelRights(
  appId: string,
  body: KernelRightRequestBody,
  stepUpId: string | null,
): Promise<KernelRightRequestView> {
  return apiFetch<KernelRightRequestView>(
    `${appKernelRightsPath(appId)}/requests`,
    writeInit('POST', { rights: body.rights, justification: body.justification }, stepUpId),
  );
}

/** `/v1/apps/<appId>/release-sources`, with the id encoded as one segment. Pure. */
export function releaseSourcesPath(appId: string): string {
  return `/v1/apps/${encodeURIComponent(appId)}/release-sources`;
}

/**
 * Read the live release sources of one app, oldest first: `GET /v1/apps/:appId/release-sources`.
 *
 * @throws ApiError on a non-2xx answer.
 */
export function getReleaseSources(appId: string): Promise<readonly ReleaseSourceView[]> {
  return api.get<readonly ReleaseSourceView[]>(releaseSourcesPath(appId));
}

/**
 * Replace the release sources of one app: `PUT /v1/apps/:appId/release-sources` with `{ sources }`.
 * An empty list removes every live source. The app needs no code-signing key (R-004).
 *
 * release-engine runs its refusals before the step-up, so a refusal on the first send opens no
 * step-up request:
 *
 * 1. 409 `release_source_duplicate` for a repeated source.
 * 2. 403 `release_source_needs_platform_admin`, with the `repository`, `refPattern` and
 *    `workflowPath` of the first added source, when the list adds a source that is not live on the
 *    app and the caller holds no `cp:platform:admin` (R-019). A removal, or the live list sent
 *    again, needs no platform administrator.
 * 3. 409 `release_source_claimed`, with the `repository`, `refPattern` and `workflowPath` of the
 *    first such source, when an added source admits a run that a live source of another app admits.
 *
 * release-engine runs refusals 2 and 3 again on the second send, after the approved step-up. So a
 * source that left the live set during the step-up, or a claim that another app committed in that
 * time, gives the 403 or the 409 after the caller approved the step-up.
 *
 * @param stepUpId `null` on the first send; the approved step-up request id on the second.
 * @returns the live sources after the change.
 * @throws as {@link requestKernelRights}, and the three refusals above.
 */
export async function putReleaseSources(
  appId: string,
  sources: readonly ReleaseSourceInput[],
  stepUpId: string | null,
): Promise<readonly ReleaseSourceView[]> {
  const body = {
    sources: sources.map((source) => ({
      repository: source.repository,
      refPattern: source.refPattern,
      workflowPath: source.workflowPath,
    })),
  };
  return apiFetch<readonly ReleaseSourceView[]>(releaseSourcesPath(appId), writeInit('PUT', body, stepUpId));
}
